<template>
    <div :class="requiresMargin ? `mb-4` : ''">
        <label class="text-primary-200 mb-3 flex items-center justify-between gap-3 uppercase tracking-wide" :for="id" v-if="labelStr">
            <div class="flex items-center gap-2">
                <t :path="labelStr" />

                <div v-if="helpTip">
                    <fa :icon="['fas', 'circle-question']" fixed-size v-floating-tooltip="helpTip" />
                </div>
            </div>

            <slot name="labelSuffix" />
        </label>

        <div class="flex items-center" :class="{ 'group relative': sensitive }">
            <div
                v-if="prefix"
                class="bg-primary-400 text-primary-200 flex h-[2.4375rem] items-center rounded-l border-r border-white/10 px-3 text-sm"
            >
                <t :path="prefix" />
            </div>

            <span class="flex-grow" v-floating-tooltip="tippy" v-if="permission" tabindex="0">
                <input
                    class="input"
                    :class="[prefix ? '!rounded-l-none' : '', suffix ? '!rounded-r-none' : '', ...inputClasses]"
                    v-model="input"
                    :id="id"
                    :type="type"
                    :name="name"
                    :step="step"
                    :placeholder="placeholder ? t(placeholder) : ''"
                    :disabled="!hasPerms || inputDisabled"
                    :readonly="readonly"
                    :checked="checked"
                    :autofocus="autoFocus"
                    :accept="accept"
                    @blur="blur"
                    @input="emitUpdate"
                    @keydown="emitKeyDown"
                    @keyup="emitKeyUp"
                />
            </span>

            <input
                class="input"
                :class="[prefix ? '!rounded-l-none' : '', suffix ? '!rounded-r-none' : '', ...inputClasses]"
                v-model="input"
                :id="id"
                :type="type"
                :name="name"
                :step="step"
                :placeholder="placeholder ? t(placeholder) : ''"
                :disabled="inputDisabled"
                :readonly="readonly"
                :autofocus="autoFocus"
                @blur="blur"
                @change="change"
                @input="emitUpdate"
                @keydown="emitKeyDown"
                @keyup="emitKeyUp"
                v-else
            />

            <div
                v-if="suffix"
                class="bg-primary-400/40 text-primary-200 flex h-[2.4375rem] items-center rounded-r border-l border-white/10 px-3 text-sm"
            >
                <t :path="suffix" />
            </div>

            <div v-if="$slots.default" class="ml-4 shrink-0">
                <slot />
            </div>

            <!--
                Sensitive overlay: renders a same-length string of random glyphs over the input,
                blurred and faded out on hover/focus. The mask defeats deblur attacks because the
                pixels under the blur belong to junk text, not the real value.
            -->
            <div
                v-if="sensitive"
                aria-hidden="true"
                class="bg-primary-500 text-primary-200 blur-xs pointer-events-none absolute inset-0 flex select-none items-center overflow-hidden whitespace-nowrap rounded-md px-4 py-2.5 opacity-100 transition-opacity duration-150 group-focus-within:opacity-0 group-active:opacity-0"
            >
                {{ maskedValue }}
            </div>
        </div>

        <p class="small text-primary-300 mt-1" v-if="footer">
            <t :path="footer" />
        </p>

        <div class="text-danger-700" v-if="inputErrors && !formGlobal">
            <p v-for="(error, idx) of inputErrors" :key="idx">
                <t :path="error" />
            </p>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, onBeforeMount, onBeforeUnmount, PropType, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Validator } from '~/core';
import { translateRequiresPermissions, usePermissionsStore } from '~/plugins/permission';
// TranslatableMessage is globally declared

export default defineComponent({
    name: 'FormInput',

    emits: ['update:value', 'keydown', 'keyup'],
    props: {
        name: {
            type: String,
            required: true,
        },
        value: {
            type: [String, Number],
        },
        resultPrefix: {
            type: String,
        },
        placeholder: {
            type: String,
        },
        label: {
            type: [String, Array],
        },
        hideLabel: {
            type: Boolean,
        },
        prefix: {
            type: [String, Array],
        },
        suffix: {
            type: [String, Array],
        },
        noMargin: {
            type: Boolean,
        },
        footer: {
            type: String,
        },
        type: {
            type: String,
        },
        step: {
            type: [String, Number],
        },
        rule: {
            type: String,
        },
        disabled: {
            type: Boolean,
        },
        checked: {
            type: Boolean,
        },
        readonly: {
            type: Boolean,
        },
        permission: {
            type: [String, Array] as PropType<string | string[]>,
        },
        autoFocus: {
            type: Boolean,
            default: false,
        },
        accept: {
            type: String,
        },
        inputClasses: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
        helpTip: {
            type: String,
        },
        tooltipPlacement: {
            type: String,
            validator: () => true, // TODO: List of allowed placements - cba writing long array rn
            default: 'bottom',
        },
        sensitive: {
            type: Boolean,
            default: false,
        },
    },

    setup(props, { emit }) {
        const permissions = usePermissionsStore();
        const { t } = useI18n();
        const localId = useId();

        const input = ref(props.value ?? '');
        const update = (newValue: any) => {
            if (newValue === undefined) return;

            input.value = newValue;
        };
        watch(() => props.value, update);

        const maskedValue = ref('');
        const maskChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        watch(
            () => [props.sensitive, input.value] as const,
            ([sensitive, value]) => {
                if (!sensitive) {
                    maskedValue.value = '';
                    return;
                }

                const length = String(value ?? '').length;
                const bytes = crypto.getRandomValues(new Uint8Array(length));
                maskedValue.value = Array.from(bytes, (b) => maskChars[b % maskChars.length]).join('');
            },
            { immediate: true },
        );

        const inputDisabled = ref(!!props.disabled);
        watch(
            () => props.disabled,
            (newDisabled) => {
                inputDisabled.value = !!newDisabled;
            },
        );
        const inputErrors = ref<null | TranslatableMessage[][]>(null);

        const labelStr = computed(() => {
            if (props.type === 'hidden' || props.hideLabel) return null;
            if (props.label) return props.label;

            return `components.form.fields.${props.name}`;
        });

        const formGlobal = inject<boolean>('formGlobal', false);
        const displayFormErrors = inject<displayFormErrorsFn | null>('displayFormErrors', null);
        const validate = () => {
            if (!props.rule) return;

            const { errors } = Validator.formValidate(props.name, input.value, props.rule, formGlobal);

            inputErrors.value = errors || [];
            displayFormErrors?.();
        };

        let unregister: any;

        onBeforeMount(() => {
            unregister = inject<registerFormComponentFn | null>(
                'registerFormComponent',
                null,
            )?.({
                key: props.name,
                value: computed(() => (props.resultPrefix || '') + input.value),
                errors: inputErrors,
                validate,
                rule: props.rule,
                onSubmit: (state) => {
                    if (props.disabled) return;

                    inputDisabled.value = state;
                },
            });
        });

        onBeforeUnmount(() => {
            unregister?.();
            unregister = null;
        });

        const hasPerms = computed(() => (props.permission ? permissions.has(props.permission) : true));
        return {
            t,
            input,
            maskedValue,
            labelStr,
            formGlobal,
            inputErrors,
            inputDisabled,
            id: computed(() => `${localId}-${props.name}-input`),
            requiresMargin: computed(() => {
                if (props.type === 'hidden' || props.hideLabel || props.noMargin) {
                    return false;
                }

                return true;
            }),
            change: (evt: Event) => {
                /*
                 * Autofill on iOS triggers this event, so we need to check whether the value was changed
                 * and update it if so.
                 *
                 * @see https://github.com/wisp-gg/frontend/issues/61
                 */
                const target = evt.target as HTMLInputElement;
                if (input.value !== target.value) input.value = target.value;
            },
            blur: () => validate(),
            emitUpdate: () => emit('update:value', input.value),
            emitKeyDown: (evt: KeyboardEvent) => emit('keydown', evt),
            emitKeyUp: (evt: KeyboardEvent) => emit('keyup', evt),

            hasPerms,
            tippy: computed(() => {
                if (props.permission && !hasPerms.value) {
                    return [translateRequiresPermissions(props.permission), props.tooltipPlacement, true];
                }

                return null;
            }),
        };
    },
});
</script>
